import { Flex, Spacer, useDisclosure, useToast } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { ItemsInner, PublicationVersionFileModel } from "@nens/rana/api-v1";
import { RanaModal, RanaTable } from "@nens/ranalib";
import { removeNullUndefined, useSelection } from "@nens/shared-utils";

import { FoldersBreadcrumb } from "@/components/folders-breadcrumb";
import { TableEmptyFiles } from "@/components/table-empty-file";
import { useProjectFiles } from "@/hooks/use-project-files";
import { RanaFileIcon } from "@/lib/rana-file-data-type";
import { useFileDescriptorQueryDirectly } from "@/queries/query-file-descriptors";
import { fileNameColumnString } from "@/translations";
import { getNameFromFileId } from "@/utils/get-name-from-file-id";

import { getFiles } from "../../../store/selectors";
import { usePublicationStore } from "../../../store/store";
import { CompleteFile } from "../../../types/file-reference-with-file";

import { FileAddedScenarioModal } from "./file-added-scenario-modal";

const isSameFile = (selectedItem: PublicationVersionFileModel, item: PublicationVersionFileModel) =>
  selectedItem.id === item.id;

const isScenarioFile = ({ file }: CompleteFile | { file: PublicationVersionFileModel }) =>
  file.descriptor?.dataType === "scenario";

const getDisabledFileTitle = ({
  fileOrDirectory,
  publicationFileReferences,
  intl,
}: {
  fileOrDirectory: ItemsInner;
  publicationFileReferences: { path: string }[];
  intl: ReturnType<typeof useIntl>;
}): string | undefined => {
  const { id, type, descriptor } = fileOrDirectory;

  // Folders are always enabled, so we only need to check files
  if (type !== "file") return;

  const isAlreadyAdded = publicationFileReferences.some(({ path }) => path === id);
  const isNotCompleted = descriptor?.status.id !== "completed";
  const isShapefile = descriptor?.dataType === "shapefile";

  if (isAlreadyAdded)
    return intl.formatMessage({
      id: "publication.file_added_scenario.file_already_added",
      defaultMessage: "File is already added",
    });
  if (isNotCompleted)
    return intl.formatMessage({
      id: "publication.file_added_scenario.file_not_completed",
      defaultMessage: "File is loading or invalid and cannot be added yet",
    });
  if (isShapefile)
    return intl.formatMessage({
      id: "publication.file_added_scenario.shapefiles_not_supported",
      defaultMessage: "Shapefiles are not supported",
    });
};

export const FileAddModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const intl = useIntl();
  const toast = useToast();
  const publicationFileReferences = usePublicationStore(getFiles);
  const fetchFileDescriptor = useFileDescriptorQueryDirectly();
  const { paths, setPaths, files, tableDataIds, onDirectoryOrFileClick } = useProjectFiles({});
  const [selected, toggleSelected, emptySelected] = useSelection<PublicationVersionFileModel>(
    [],
    isSameFile
  );

  const convertToCompleteFile = async (
    file: PublicationVersionFileModel
  ): Promise<CompleteFile | undefined> => {
    const { data: descriptor } = await fetchFileDescriptor(file.descriptorId);

    if (!descriptor) {
      return undefined;
    }

    return {
      path: file.id,
      file: {
        ...file,
        descriptor,
      },
    };
  };

  const addPublicationFiles = usePublicationStore((state) => state.addPublicationFiles);

  const addedScenarioModalDisclosure = useDisclosure();

  const handleAddFiles = async (dontOpenScenarioModal?: boolean) => {
    const allCompleteFiles = await Promise.all(selected.map(convertToCompleteFile));
    const completeFiles = removeNullUndefined(allCompleteFiles);

    if (allCompleteFiles.length !== completeFiles.length) {
      toast({
        title: intl.formatMessage({
          id: "publication.file_add_modal.some_files_unavailable",
          defaultMessage:
            "Something went wrong with fetching one or more of the files and they have not been added",
        }),
        status: "error",
      });
    }

    if (!dontOpenScenarioModal && completeFiles.some(isScenarioFile)) {
      addedScenarioModalDisclosure.onOpen();
    } else {
      addPublicationFiles(completeFiles);
      emptySelected();
      onClose();
    }
  };

  return (
    <>
      <FileAddedScenarioModal
        onAddScenarioFile={() => handleAddFiles(true)}
        scenarioFiles={selected.filter((file) => isScenarioFile({ file }))}
        {...addedScenarioModalDisclosure}
      />
      <RanaModal
        isOpen={isOpen}
        onClose={onClose}
        topBar={
          <Flex w="full" alignItems="center" justifyContent="space-between">
            <FormattedMessage id="heading.add_files" defaultMessage="Add files" />
            <Spacer />
          </Flex>
        }
        maxW="xl"
        bottomChild={intl.formatMessage(
          {
            id: "publications.amount_files_selected",
            defaultMessage: "{number} file(s) selected",
          },
          { number: selected.length }
        )}
        buttons={[
          {
            variant: "primary",
            title: selected.length
              ? intl.formatMessage({
                  id: "button.no_files_selected",
                  defaultMessage: "No files selected",
                })
              : undefined,
            isDisabled: !selected.length,
            onClick: () => handleAddFiles(),
            children: intl.formatMessage({
              id: "button.add",
              defaultMessage: "Add",
            }),
          },
        ]}
      >
        <Flex justifyContent="space-between" w="full">
          <FoldersBreadcrumb paths={paths} setPaths={setPaths} isViewingFolder />
        </Flex>

        <RanaTable
          columnDefinitions={[
            {
              width: "table_cell",
              title: fileNameColumnString(intl),
            },
          ]}
          tableDataIds={tableDataIds}
          emptyElement={<TableEmptyFiles />}
        >
          <RanaTable.Container mt={8}>
            <RanaTable.Body>
              {files.map((fileOrDirectory) => {
                const { id, type } = fileOrDirectory;
                const name = getNameFromFileId(id);
                const isFile = type === "file";

                const disabledTitle = getDisabledFileTitle({
                  fileOrDirectory,
                  publicationFileReferences,
                  intl,
                });

                return (
                  <RanaTable.Row
                    isDisabled={!!disabledTitle}
                    title={disabledTitle}
                    isSelected={selected.some((item) => item.id === fileOrDirectory.id)}
                    cursor="pointer"
                    onClick={() => {
                      onDirectoryOrFileClick(fileOrDirectory);
                      if (isFile) toggleSelected({ ...fileOrDirectory, type: "file" });
                    }}
                    key={id}
                    rowId={id}
                  >
                    <RanaTable.Cell title={name}>
                      <RanaFileIcon fileOrDirectory={fileOrDirectory} />
                      {name}
                    </RanaTable.Cell>
                  </RanaTable.Row>
                );
              })}
            </RanaTable.Body>
          </RanaTable.Container>
        </RanaTable>
      </RanaModal>
    </>
  );
};
